const CAP3 = {
  id: 3,
  slug: "cap3-inmutabilidad-y-finalidad",
  title: "Capítulo 3 — Inmutabilidad y finalidad en las transacciones",
  sections: [
    {
      heading: "¿Qué es la inmutabilidad en blockchain?",
      text: [
        "Cuando decimos que una blockchain es **inmutable**, nos referimos a que el historial de transacciones no se puede cambiar de forma sencilla ni discreta.",
        "En la práctica, esto significa que una vez que una transacción ha sido incluida en un bloque y ese bloque ha sido aceptado por la red, **no está pensado para ser editado, borrado ni reemplazado**.",
        "Esta característica es una de las grandes diferencias frente a bases de datos tradicionales, donde un administrador puede modificar registros con permisos especiales."
      ]
    },
    {
      heading: "Elementos que refuerzan la inmutabilidad",
      text: [
        "La inmutabilidad no aparece por arte de magia. Es el resultado de varias piezas técnicas que trabajan juntas:"
      ],
      list: [
        "**Estructura en forma de cadena:** cada bloque contiene un **hash** (huella criptográfica) del bloque anterior. Si alguien modifica un bloque pasado, los hashes dejan de coincidir y la cadena se rompe.",
        "**Algoritmos de consenso:** mecanismos como Proof of Work o Proof of Stake hacen que alterar el historial sea muy costoso en términos de energía, tiempo o capital.",
        "**Descentralización:** los datos se copian en muchos nodos repartidos por el mundo. Para cambiar algo de forma efectiva, habría que convencer (o controlar) a la mayoría de ellos, lo que en redes grandes es extremadamente difícil."
      ]
    },
    {
      heading: "Ventajas de la inmutabilidad",
      text: [
        "La inmutabilidad aporta varias ventajas clave en el diseño de sistemas basados en blockchain:"
      ],
      list: [
        "Garantiza la **integridad de los datos**: lo que se registró, así se mantiene.",
        "Fomenta la **transparencia**, especialmente en redes públicas donde cualquiera puede revisar el historial.",
        "Reduce la posibilidad de **fraude**, ya que es muy difícil manipular registros antiguos sin que se note.",
        "Ayuda a generar **confianza** entre participantes que no se conocen, porque ninguno puede editar el libro contable a su antojo."
      ]
    },
    {
      heading: "Ejemplos de uso de la inmutabilidad",
      text: [
        "La inmutabilidad es útil más allá de las criptomonedas. Algunos ejemplos:"
      ],
      list: [
        "Rastreo de productos en **cadenas de suministro**: verificar el origen, recorrido y estado de un bien.",
        "Registro de **títulos académicos** o certificaciones profesionales.",
        "Almacenamiento de **evidencias digitales**: documentos legales, contratos, acuerdos.",
        "Gestión de **historias clínicas** o datos sensibles donde se desea un registro auditable."
      ]
    },
    {
      heading: "¿Qué es la finalidad de una transacción?",
      text: [
        "La **finalidad** (finality) es el momento a partir del cual una transacción se considera **definitiva**: ya no se revertirá ni se reemplazará por otra.",
        "En otras palabras, es el punto en el que puedes estar razonablemente seguro de que los fondos que recibiste no serán “deshechos” por una reorganización de la cadena o por un ataque.",
        "Inmutabilidad y finalidad están relacionadas: la finalidad marca cuándo una transacción entra en el terreno práctico de la inmutabilidad."
      ]
    },
    {
      heading: "Tipos de finalidad en distintas redes",
      text: [
        "No todas las blockchains manejan la finalidad de la misma forma. Existen varios enfoques:"
      ],
      subsections: [
        {
          heading: "Finalidad probabilística",
          text: [
            "En algunas redes, una transacción es cada vez más segura **a medida que se añaden nuevos bloques encima**.",
            "Cada bloque adicional hace menos probable que la red reorganice la cadena hasta ese punto.",
            "Ejemplo típico: esperar un número determinado de confirmaciones antes de considerar que un pago está “seguro”."
          ]
        },
        {
          heading: "Finalidad económica",
          text: [
            "En redes que usan mecanismos tipo Proof of Stake, comportarse de forma deshonesta puede implicar **pérdidas económicas** importantes para los validadores (por ejemplo, perder parte del stake bloqueado).",
            "La finalidad aquí se apoya en la idea de que sería muy costoso para un validador intentar revertir transacciones válidas, porque arriesga su propio capital."
          ]
        },
        {
          heading: "Finalidad absoluta o determinística",
          text: [
            "En algunos diseños, una vez que un bloque es validado por un conjunto suficiente de nodos o validadores, se considera **inmutable desde ese instante**.",
            "No se espera que la cadena se reorganice más atrás de ese punto, por lo que la transacción alcanza finalidad inmediata o casi inmediata."
          ]
        },
        {
          heading: "Finalidad instantánea en redes controladas",
          text: [
            "En redes privadas o muy permisadas, la finalidad puede ser casi **instantánea**, porque un grupo pequeño y conocido de validadores decide rápidamente qué bloques se aceptan.",
            "Esto sacrifica descentralización, pero ofrece rapidez para casos de uso específicos (pagos internos, sistemas cerrados, etc.)."
          ]
        }
      ]
    },
    {
      heading: "¿Por qué importa la finalidad?",
      text: [
        "La finalidad es crucial para el uso práctico de una blockchain:",
        "Si no sabes cuándo una transacción es realmente definitiva, es difícil usarla para pagos, liquidación de operaciones, entrega de productos o registro de información crítica."
      ],
      list: [
        "Aporta **certeza** a las partes: una vez alcanzada la finalidad, la operación se considera concluida.",
        "Previene disputas sobre si un pago fue válido o no.",
        "Reduce el riesgo de ataques como el **doble gasto**, donde alguien intenta gastar las mismas monedas dos veces."
      ]
    },
    {
      heading: "Ataques que amenazan inmutabilidad y finalidad",
      text: [
        "Aunque la mayoría de las blockchains bien diseñadas son muy resistentes, existen modelos teóricos y prácticos de ataque que intentan romper la inmutabilidad o retrasar la finalidad."
      ],
      subsections: [
        {
          heading: "Ataque del 51%",
          text: [
            "Ocurre cuando un actor (o grupo) controla más del 50% del poder de validación (por ejemplo, poder de minería en PoW).",
            "Con ese control podría intentar reorganizar bloques recientes, censurar transacciones o intentar un doble gasto.",
            "En redes grandes y consolidadas esto es extremadamente caro y difícil de coordinar, pero en redes pequeñas es un riesgo real."
          ]
        },
        {
          heading: "Ataques de carrera y doble gasto simple",
          text: [
            "En un ataque de carrera, una persona envía dos transacciones en conflicto casi al mismo tiempo (por ejemplo, pagando con las mismas monedas en dos lugares distintos).",
            "La idea es que una de las transacciones sea aceptada por la víctima, mientras que la otra termina incluida en la cadena final, invalidando la primera.",
            "Por eso muchas aplicaciones esperan varias confirmaciones antes de aceptar un pago como definitivo."
          ]
        },
        {
          heading: "Ataques con bloques preminados",
          text: [
            "En algunos escenarios teóricos, un atacante puede minar en secreto una cadena alternativa con transacciones diferentes, y luego intentar publicarla para reemplazar parte de la cadena oficial.",
            "Si la cadena oculta resulta ser más larga o tener más trabajo acumulado, podría reorganizar transacciones recientes.",
            "Este tipo de ataque se vuelve poco viable cuando se esperan suficientes confirmaciones."
          ]
        },
        {
          heading: "Ataques por fuerza bruta",
          text: [
            "Un ataque por fuerza bruta intentaría encontrar variantes fraudulentas de la cadena probando combinaciones hasta hallar una que cumpla las reglas de consenso.",
            "En la práctica, la complejidad criptográfica y el costo computacional hacen que esto sea **inviable** en redes bien diseñadas."
          ]
        }
      ]
    },
    {
      heading: "Resumen del capítulo",
      text: [
        "La **inmutabilidad** asegura que el historial de una blockchain no se pueda cambiar fácilmente, mientras que la **finalidad** marca el momento en que una transacción se considera definitiva.",
        "Ambos conceptos trabajan juntos para ofrecer algo muy valioso: un registro compartido donde los participantes pueden confiar en que lo que ven hoy no será reescrito mañana.",
        "Comprender inmutabilidad y finalidad te ayudará a evaluar mejor qué tan segura es una blockchain para distintos usos: pagos, registros, contratos o cualquier otro tipo de dato crítico."
      ]
    }
  ]
};

export default CAP3;
